/**
 * Pre-process an image client-side so Instagram's Graph API can ingest it
 * without any of its own re-encoding or cropping passes. The goal is that
 * every byte we send through Zernio lands on IG's servers unchanged.
 *
 * Meta's IG container endpoint accepts:
 *   - JPEG only (no PNG/WebP/GIF — those get re-encoded)
 *   - sRGB color space
 *   - Width 320–1440 px (anything wider is downscaled)
 *   - Aspect ratio 0.8 (4:5) to 1.91 (1.91:1) — outside that gets cropped
 *   - File size ≤ 8 MB (over → Zernio recompresses)
 *
 * We normalise inside all four windows on the client. Reports the
 * transformation so the UI can surface what changed.
 */

export interface OptimizeReport {
  transformed: boolean;
  reason?:
    | "non-image"
    | "no-change"
    | "normalized"
    | "resized"
    | "transcoded"
    | "cropped"
    | "resized+transcoded"
    | "cropped+resized"
    | "cropped+transcoded"
    | "cropped+resized+transcoded";
  fromDimensions?: { width: number; height: number };
  toDimensions?: { width: number; height: number };
  fromBytes: number;
  toBytes: number;
  fromType: string;
  toType: string;
  cropped: boolean;
}

export interface OptimizeResult {
  file: File;
  report: OptimizeReport;
}

// Meta's hard limits (any change here = silent re-encode by IG).
const MAX_WIDTH = 1440;
const MAX_BYTES = 8 * 1024 * 1024;
const MIN_ASPECT = 0.8; // 4:5
const MAX_ASPECT = 1.91; // 1.91:1
const JPEG_QUALITY_PRIMARY = 0.9;
const JPEG_QUALITY_FALLBACK = 0.85;
const OUTPUT_MIME = "image/jpeg";

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  originalName: string,
  quality: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas toBlob returned null"));
          return;
        }
        const base = originalName.replace(/\.[^.]+$/, "");
        resolve(new File([blob], `${base}.jpg`, { type: OUTPUT_MIME }));
      },
      OUTPUT_MIME,
      quality,
    );
  });
}

interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

function computeAspectCrop(width: number, height: number): {
  rect: CropRect;
  cropped: boolean;
} {
  const aspect = width / height;
  if (aspect >= MIN_ASPECT && aspect <= MAX_ASPECT) {
    return { rect: { sx: 0, sy: 0, sw: width, sh: height }, cropped: false };
  }
  // Center-crop to the nearest valid bound.
  if (aspect > MAX_ASPECT) {
    // Too wide: crop horizontally to MAX_ASPECT.
    const newW = Math.round(height * MAX_ASPECT);
    const sx = Math.round((width - newW) / 2);
    return { rect: { sx, sy: 0, sw: newW, sh: height }, cropped: true };
  }
  // Too tall: crop vertically to MIN_ASPECT.
  const newH = Math.round(width / MIN_ASPECT);
  const sy = Math.round((height - newH) / 2);
  return { rect: { sx: 0, sy, sw: width, sh: newH }, cropped: true };
}

export async function optimizeImageForUpload(file: File): Promise<OptimizeResult> {
  const baseReport: OptimizeReport = {
    transformed: false,
    fromBytes: file.size,
    toBytes: file.size,
    fromType: file.type,
    toType: file.type,
    cropped: false,
  };

  if (!file.type.startsWith("image/")) {
    return { file, report: { ...baseReport, reason: "non-image" } };
  }
  // Animated formats — can't go through canvas without losing frames.
  if (file.type === "image/gif") {
    return { file, report: { ...baseReport, reason: "non-image" } };
  }

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    return { file, report: { ...baseReport, reason: "non-image" } };
  }

  const fromDimensions = { width: img.naturalWidth, height: img.naturalHeight };
  const { rect, cropped } = computeAspectCrop(img.naturalWidth, img.naturalHeight);

  // Post-crop dimensions; then clamp width to MAX_WIDTH.
  const ratio = rect.sw > MAX_WIDTH ? MAX_WIDTH / rect.sw : 1;
  const toWidth = Math.round(rect.sw * ratio);
  const toHeight = Math.round(rect.sh * ratio);
  const wasResized = ratio < 1;
  const wasTranscoded = file.type !== OUTPUT_MIME;

  const canvas = document.createElement("canvas");
  canvas.width = toWidth;
  canvas.height = toHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return {
      file,
      report: {
        ...baseReport,
        fromDimensions,
        toDimensions: fromDimensions,
        reason: "no-change",
      },
    };
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, toWidth, toHeight);

  let outFile = await canvasToFile(canvas, file.name, JPEG_QUALITY_PRIMARY);
  if (outFile.size > MAX_BYTES) {
    outFile = await canvasToFile(canvas, file.name, JPEG_QUALITY_FALLBACK);
  }

  // If absolutely nothing changed (rare: input was already a perfectly-sized
  // sRGB baseline JPEG within all bounds), keep the original so we don't pay
  // a quality-cycle tax for no benefit.
  const noVisibleChange =
    !cropped &&
    !wasResized &&
    !wasTranscoded &&
    file.size <= MAX_BYTES &&
    file.type === OUTPUT_MIME;
  if (noVisibleChange) {
    return {
      file,
      report: {
        ...baseReport,
        fromDimensions,
        toDimensions: fromDimensions,
        reason: "no-change",
      },
    };
  }

  let reason: OptimizeReport["reason"];
  if (cropped && wasResized && wasTranscoded) reason = "cropped+resized+transcoded";
  else if (cropped && wasResized) reason = "cropped+resized";
  else if (cropped && wasTranscoded) reason = "cropped+transcoded";
  else if (wasResized && wasTranscoded) reason = "resized+transcoded";
  else if (cropped) reason = "cropped";
  else if (wasResized) reason = "resized";
  else if (wasTranscoded) reason = "transcoded";
  else reason = "normalized";

  return {
    file: outFile,
    report: {
      transformed: true,
      reason,
      fromDimensions,
      toDimensions: { width: toWidth, height: toHeight },
      fromBytes: file.size,
      toBytes: outFile.size,
      fromType: file.type,
      toType: OUTPUT_MIME,
      cropped,
    },
  };
}

export function describeOptimization(r: OptimizeReport): string | null {
  if (!r.transformed) return null;
  const from = r.fromDimensions;
  const to = r.toDimensions;
  const sizeFromMb = (r.fromBytes / 1024 / 1024).toFixed(2);
  const sizeToMb = (r.toBytes / 1024 / 1024).toFixed(2);
  const dims = from && to ? `${from.width}×${from.height} → ${to.width}×${to.height}` : "";
  const cropNote = r.cropped ? " (center-cropped to IG aspect)" : "";
  return `Optimized for upload${cropNote}: ${dims} · ${sizeFromMb}MB → ${sizeToMb}MB`;
}
